// @ts-nocheck
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import objectKeys from "@store/Framework/Common/Utilities/objectKeys";
import InvoiceHistoryState from "@store/Framework/Store/Greif/Pages/InvoiceHistory/InvoiceHistoryState";
import { Draft } from "immer";
import isEqual from "lodash/isEqual";

type ApiParameters = GetInvoicesApiParameter;
type State = InvoiceHistoryState;

interface FilterParameterGroup<ParameterType> {
    keys: Array<keyof ParameterType>;
    parentKey?: keyof ParameterType;
}

// Empty filterable parameters values
// Add values of filterable parameters when they should be considered inactive, all filterable parameters must be defined here.
const emptyFilterParameter: ApiParameters = {
    invoiceNumber: "",
    poNumber: "",
    fromDate: "",
    toDate: "",
    fromDueDate: "",
    toDueDate: "",
    showInvoiceCredit: false,
    showInvoiceNotPaid: false,
    showInvoiceOverdue: false,
    showInvoicePartiallyPaid: false,
    showInvoicePaid: false,
};

// Default filterable parameters values
// Add default values of filterable parameters here if they should differ from their empty values and be MAINTAINED when reseting filters.
const defaultFilterParameter: ApiParameters = {
    ...emptyFilterParameter,
};

// Initial filterable parameters values
// Add initial values of filterable parameters here if they should differ from their empty values and should be EMPTIED when reseting filters.
const initialFilterParameter: ApiParameters = {
    ...defaultFilterParameter,
};

// Grouped filterable parameters
// For example: a date range with from/to parameters where both parameters should be populated to be considered as one applied filter.
const filterParameterGroups: FilterParameterGroup<ApiParameters>[] = [];

// Initial parameters
// Add any default values for non-filterable parameters here if needed
const initialParameter: ApiParameters = {
    ...initialFilterParameter,
    sort: "invoiceDate DESC",
};

// Validate if a provided parameter value is considered empty
const isParameterEmpty = (parameter: ApiParameters, key: keyof ApiParameters) => {
    return (
        parameter[key] === undefined || parameter[key] === null || isEqual(parameter[key], emptyFilterParameter[key])
    );
};

// Cleanup the parameter object by removing entries that are considered empty
const cleanupParameter = (parameter: ApiParameters) => {
    const result = { ...parameter };

    filterParameterGroups.forEach(({ keys, parentKey }) => {
        if (parentKey) {
            const hasEmpty = keys.some(key => isParameterEmpty(result, key));

            if (hasEmpty) {
                keys.forEach(key => delete result[key]);
            }
        }
    });

    objectKeys(result)?.forEach(key => {
        if (isParameterEmpty(result, key)) {
            delete result[key];
        }
    });

    return result;
};

const cleanEmptyFilterParameter = cleanupParameter(emptyFilterParameter);
const cleanDefaultFilterParameter = cleanupParameter(defaultFilterParameter);

// Boolean that determines if the user has applied filters by themselves
const getHasFilters = (parameter: ApiParameters) => {
    return (
        objectKeys(defaultFilterParameter)?.some(key => !isEqual(parameter[key], cleanDefaultFilterParameter[key])) ??
        false
    );
};

// Returns cleared draft parameters
const getClearedDraftParameter = (draft: Draft<State>) => {
    return {
        ...draft.parameter, // maintain active non-filterable parameters
        ...defaultFilterParameter, // reset filterable parameters to their default values
        ...draft.defaultParameter, // apply default values from external sources
    };
};

// Updates other state variables related to parameter changes
const updateParameterVariables = (draft: Draft<State>) => {
    // This function should be called in all actions that modify "parameter" or "draftParameter"
    const cleanDraftParameter = cleanupParameter(draft.draftParameter);
    const clearedDraftParameter = getClearedDraftParameter(draft);
    const cleanClearedDraftParameter = cleanupParameter(clearedDraftParameter);
    const countGroupStatus: boolean[] = [];

    draft.hasFilters = getHasFilters(draft.parameter);
    draft.canApplyFilters = false;
    draft.canClearFilters = false;
    draft.filterCount = 0;

    objectKeys(emptyFilterParameter)?.forEach(key => {
        if (!draft.canApplyFilters && !isEqual(draft.parameter[key], cleanDraftParameter[key])) {
            draft.canApplyFilters = true;
        }

        if (!draft.canClearFilters && !isEqual(cleanDraftParameter[key], cleanClearedDraftParameter[key])) {
            draft.canClearFilters = true;
        }

        if (!isEqual(draft.parameter[key], cleanEmptyFilterParameter[key])) {
            const hasGroup = filterParameterGroups.some(({ keys }, index) => {
                const isGrouped = Boolean(keys.find(groupKey => groupKey === key));

                if (isGrouped && !countGroupStatus[index]) {
                    countGroupStatus[index] = true;
                    draft.filterCount++;
                }

                return isGrouped;
            });

            if (!hasGroup) {
                draft.filterCount++;
            }
        }
    });

    filterParameterGroups.forEach(({ parentKey, keys }) => {
        if (parentKey && !isParameterEmpty(draft.draftParameter, parentKey)) {
            if (keys.some(key => key !== parentKey && isParameterEmpty(draft.draftParameter, key))) {
                draft.canApplyFilters = false;
            }
        }
    });

    return draft;
};

const initialState: State = updateParameterVariables({
    canApplyFilters: false,
    canClearFilters: false,
    defaultParameter: {},
    draftParameter: initialParameter,
    filterCount: 0,
    hasFilters: getHasFilters(cleanDefaultFilterParameter),
    parameter: cleanupParameter(initialParameter),
});

const clearDraftParameter = (draft: Draft<State>) => {
    draft.draftParameter = getClearedDraftParameter(draft);

    // Reset to the first page whenever we are clearing active filters
    delete draft.draftParameter.page;
};

const updateDraftParameter = (draft: Draft<State>, action: { parameter: ApiParameters }) => {
    draft.draftParameter = { ...draft.draftParameter, ...action.parameter };

    // Reset to the first page whenever we change a filter except if its the page itself
    if (!action.parameter.page || action.parameter.page === 1) {
        delete draft.draftParameter.page;
    }
};

const applyDraftParameter = (draft: Draft<State>) => {
    draft.parameter = cleanupParameter(draft.draftParameter);
};

const actions = {
    "Greif/Pages/InvoiceHistory/UpdateDraftParameter": (draft: Draft<State>, action: { parameter: ApiParameters }) => {
        updateDraftParameter(draft, action);
        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/ClearDraftParameter": (draft: Draft<State>) => {
        clearDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/ApplyDraftParameter": (draft: Draft<State>) => {
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/CancelDraftParameterChanges": (draft: Draft<State>) => {
        draft.draftParameter = {
            ...emptyFilterParameter,
            ...draft.parameter,
        };

        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/SetParameter": (draft: Draft<State>, action: { parameter: ApiParameters }) => {
        const parameter = { ...emptyFilterParameter, ...action.parameter };

        filterParameterGroups.forEach(({ keys, parentKey }) => {
            if (parentKey && keys.some(key => key !== parentKey && isParameterEmpty(parameter, key))) {
                delete parameter[parentKey];
            }
        });

        updateDraftParameter(draft, { parameter });
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/ClearParameter": (draft: Draft<State>) => {
        clearDraftParameter(draft);
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/UpdateParameter": (draft: Draft<State>, action: { parameter: ApiParameters }) => {
        const parameter = { ...action.parameter };
        updateDraftParameter(draft, { parameter });
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/InvoiceHistory/UpdateDefaultParameter": (
        draft: Draft<State>,
        action: { parameter: ApiParameters },
    ) => {
        draft.defaultParameter = { ...draft.defaultParameter, ...action.parameter };
        updateParameterVariables(draft);
    },
};

const reducer = createTypedReducerWithImmer(initialState, actions);
export type GreifInvoiceHistoryPageActions = Parameters<typeof reducer>[1];
export default reducer;
